import React, { Fragment, useContext, useEffect, useState } from 'react'
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import PropTypes from 'prop-types'

import { UserStoreContext } from '../../stores/UserStore'

import {
  checkEventsAirCost,
  getAttendeeSyncHistory,
  getAttendeeSyncResumeStatus,
  markAttendeeSyncFailed,
} from '../../services/attendees.service'

dayjs.extend(relativeTime)

const IS_DEV = import.meta.env.DEV
const STUCK_THRESHOLD_MINUTES = 15

const STATUS_STYLES = {
  Completed: 'bg-green-100 text-green-800',
  Failed: 'bg-red-100 text-red-800',
  Processing: 'bg-blue-100 text-blue-800',
  Pending: 'bg-yellow-100 text-yellow-800',
  Deferring: 'bg-gray-100 text-gray-800',
  Retrying: 'bg-orange-100 text-orange-800',
}

const STATUS_LABELS = {
  Deferring: 'Skipped',
}

const PROCESSING_STATUSES = ['Processing', 'Pending', 'Retrying']

const SyncLogSlideout = ({ eventId, registrationType, open, onClose }) => {
  const { isEEUser } = useContext(UserStoreContext)

  const [syncs, setSyncs] = useState([])
  const [loading, setLoading] = useState(false)
  const [filterProcessing, setFilterProcessing] = useState(false)
  const [checkingCost, setCheckingCost] = useState(false)
  const [costCheck, setCostCheck] = useState(null)
  const [resumeStatus, setResumeStatus] = useState(null)

  const fetchSyncs = async () => {
    setLoading(true)
    const data = await getAttendeeSyncHistory(eventId)
    if (data) {
      setSyncs(data.results || data)
    }
    setLoading(false)
  }

  const fetchResumeStatus = async () => {
    const data = await getAttendeeSyncResumeStatus(eventId)
    setResumeStatus(data)
  }

  const handleCheckCost = async () => {
    setCheckingCost(true)
    const result = await checkEventsAirCost(eventId)
    setCostCheck(result)
    setCheckingCost(false)
  }

  const renderCostStatus = () => {
    if (costCheck.error) {
      return <span className="text-red-600">Error: {costCheck.error}</span>
    }
    if (costCheck.quotaExceeded) {
      return (
        <span className="text-red-600">
          Quota exceeded — {(costCheck.costRemaining ?? 0).toLocaleString()} remaining
        </span>
      )
    }
    return (
      <>
        Remaining: {(costCheck.costRemaining ?? 0).toLocaleString()}
        {' · checked '}
        {dayjs(costCheck.checkedAt).fromNow()}
      </>
    )
  }

  useEffect(() => {
    if (open && eventId) {
      fetchSyncs()
      fetchResumeStatus()
    }
  }, [open, eventId])

  const canMarkFailed = (sync) => {
    // Org users can open the sync log since EXEC-1543, but clearing a stuck sync is an ops
    // action the API keeps EE-only — don't offer them a button that 403s.
    if (!isEEUser) return false
    const inProgressStatuses = ['Processing', 'Pending', 'Retrying']
    if (!inProgressStatuses.includes(sync.status)) return false
    if (IS_DEV) return true
    const minutesRunning = dayjs().diff(dayjs(sync.createdAt), 'minute')
    return minutesRunning >= STUCK_THRESHOLD_MINUTES
  }

  const handleMarkFailed = async (syncId) => {
    const result = await markAttendeeSyncFailed(eventId, syncId)
    if (result) {
      setSyncs((prev) => prev.map((s) => (s.id === syncId ? { ...s, ...result } : s)))
    }
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/50 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex">
              <TransitionChild
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-200"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <DialogPanel className="pointer-events-auto w-[60vw] max-w-[800px]">
                  <div className="flex h-full flex-col overflow-y-auto bg-white shadow-xl">
                    {/* Header */}
                    <div className="flex items-center justify-between border-b p-4">
                      <DialogTitle className="text-lg font-bold text-gray-900">
                        Sync Log
                      </DialogTitle>
                      <button
                        type="button"
                        className="rounded-md text-gray-400 hover:text-gray-600"
                        onClick={onClose}
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>

                    {/* Resume cursor banner — surfaces when an aborted full or
                       incremental sync left a cursor behind, so the next sync
                       click would pick up mid-run rather than start fresh. */}
                    {resumeStatus?.bootstrap && (
                      <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
                        <span className="font-semibold">
                          Next sync all will resume from registration
                        </span>{' '}
                        {(resumeStatus.bootstrap.offset ?? 0).toLocaleString()}
                        {resumeStatus.bootstrap.startedAt && (
                          <>
                            {' (started '}
                            {dayjs(resumeStatus.bootstrap.startedAt).fromNow()})
                          </>
                        )}
                        .
                      </div>
                    )}
                    {resumeStatus?.incremental && (
                      <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
                        <span className="font-semibold">
                          Next update sync will resume from registration
                        </span>{' '}
                        {(
                          (resumeStatus.incremental.total ?? 0) -
                          (resumeStatus.incremental.pendingCount ?? 0)
                        ).toLocaleString()}
                        {resumeStatus.incremental.total != null && (
                          <>
                            {' of '}
                            {resumeStatus.incremental.total.toLocaleString()}
                          </>
                        )}
                        {resumeStatus.incremental.startedAt && (
                          <>
                            {' (started '}
                            {dayjs(resumeStatus.incremental.startedAt).fromNow()})
                          </>
                        )}
                        .
                      </div>
                    )}

                    {/* EventsAir cost-check (1pt ping) */}
                    {registrationType === 'EventsAir' && (
                      <div className="flex items-center justify-between gap-2 border-b px-4 py-2">
                        <button
                          type="button"
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
                          disabled={checkingCost}
                          onClick={handleCheckCost}
                        >
                          {checkingCost
                            ? 'Checking EventsAir Points…'
                            : 'Check Current EventsAir Points'}
                        </button>
                        {costCheck && (
                          <span className="text-xs text-gray-600">{renderCostStatus()}</span>
                        )}
                      </div>
                    )}

                    {/* Filter Toggle */}
                    <div className="flex items-center justify-between border-b px-4 py-2">
                      <button
                        type="button"
                        className="text-sm font-medium text-blue-600 hover:text-blue-800"
                        onClick={() => setFilterProcessing(!filterProcessing)}
                      >
                        {filterProcessing ? 'View All Syncs' : 'View Processing Syncs'}
                      </button>
                      <span className="text-xs text-gray-600">Showing last 100 Syncs</span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto px-4 py-3">
                      {loading && syncs.length === 0 && (
                        <p className="text-center text-sm text-gray-500">Loading...</p>
                      )}

                      {!loading && syncs.length === 0 && (
                        <p className="text-center text-sm text-gray-500">No sync history found.</p>
                      )}

                      <div className="space-y-3">
                        {syncs
                          .filter(
                            (sync) =>
                              !filterProcessing || PROCESSING_STATUSES.includes(sync.status),
                          )
                          .map((sync) => (
                            <div key={sync.id} className="rounded-lg border border-gray-200 p-3">
                              <div className="mb-2 flex items-center justify-between">
                                <span
                                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                    STATUS_STYLES[sync.status] || 'bg-gray-100 text-gray-800'
                                  }`}
                                >
                                  {STATUS_LABELS[sync.status] || sync.status}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {dayjs(sync.createdAt).format('MM/DD/YYYY h:mm A')}
                                </span>
                              </div>

                              <p className="mb-1 text-sm font-medium text-gray-900">
                                {sync.syncType}
                              </p>

                              {sync.statusMessage && (
                                <p className="mb-1 text-xs text-gray-600">{sync.statusMessage}</p>
                              )}

                              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                                {sync.totalAttendeeCount != null && (
                                  <span>Processed: {sync.totalAttendeeCount}</span>
                                )}
                                {sync.failedAttendeeCount > 0 && (
                                  <span className="text-red-600">
                                    Failed: {sync.failedAttendeeCount}
                                  </span>
                                )}
                                {sync.apiCallCount != null && (
                                  <span>API Calls: {sync.apiCallCount}</span>
                                )}
                                {sync.costConsumed != null && (
                                  <span>Cost Used: {sync.costConsumed.toLocaleString()}</span>
                                )}
                                {sync.costRemaining != null && (
                                  <span>
                                    Cost Remaining: {sync.costRemaining.toLocaleString()}
                                  </span>
                                )}
                              </div>

                              {canMarkFailed(sync) && (
                                <button
                                  type="button"
                                  className="mt-2 text-xs font-medium text-red-600 hover:text-red-800"
                                  onClick={() => handleMarkFailed(sync.id)}
                                >
                                  Mark as Failed
                                </button>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

SyncLogSlideout.propTypes = {
  eventId: PropTypes.string.isRequired,
  registrationType: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default SyncLogSlideout
